// Rarifex Fee Calculator
// Fee structure: 8% all-inclusive (includes Rarifex platform fee + payment processing)
// The crafter enters the FINAL price (what the customer sees)
// During LAUNCH MODE: Rarifex commission is 0% (crafter keeps everything except Stripe fees).
// NO discount is applied to the customer-facing price (removed 2026-07).

const RARIFEX_FEE_PERCENTAGE = 0.08; // 8% all-inclusive
const LAUNCH_DISCOUNT_PERCENTAGE = 0; // Deprecated: launch discount removed

/**
 * Check if launch mode is active (no Rarifex fee + customer discount)
 * Reads from localStorage (should be synced with DB on app load)
 */
export const isLaunchModeActive = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('rarifex_launch_mode') === 'true';
};

/**
 * Get current fee percentage (0% in launch mode, 8% otherwise)
 * @param {boolean} launchModeOverride - Optional override for launch mode state
 */
export const getCurrentFeePercentage = (launchModeOverride = null) => {
  const isLaunch = launchModeOverride !== null ? launchModeOverride : isLaunchModeActive();
  return isLaunch ? 0 : RARIFEX_FEE_PERCENTAGE;
};

/**
 * Get launch discount percentage for customers (8% during launch, 0% otherwise)
 * @param {boolean} launchModeOverride - Optional override for launch mode state
 */
export const getLaunchDiscountPercentage = (launchModeOverride = null) => {
  const isLaunch = launchModeOverride !== null ? launchModeOverride : isLaunchModeActive();
  return isLaunch ? LAUNCH_DISCOUNT_PERCENTAGE : 0;
};

/**
 * Calculate the final price for customers including all discounts
 * @param {number} basePrice - The price entered by the crafter
 * @param {number} crafterDiscount - Optional crafter discount percentage (0-100)
 * @param {boolean} launchModeOverride - Optional override for launch mode state
 * @returns {number} - The final price for customers (with crafter discount + launch discount)
 */
export const calculateFinalPrice = (basePrice, crafterDiscount = 0, launchModeOverride = null) => {
  if (!basePrice || basePrice <= 0) return 0;
  const price = parseFloat(basePrice);
  const crafterDiscountPercent = crafterDiscount ? parseFloat(crafterDiscount) / 100 : 0;
  const launchDiscountPercent = getLaunchDiscountPercentage(launchModeOverride);
  
  // Apply crafter discount first, then launch discount
  let discountedPrice = price * (1 - crafterDiscountPercent);
  discountedPrice = discountedPrice * (1 - launchDiscountPercent);
  
  return Math.round(discountedPrice * 100) / 100;
};

/**
 * Calculate the total discount percentage (crafter + launch combined)
 * @param {number} crafterDiscount - Crafter discount percentage (0-100)
 * @param {boolean} launchModeOverride - Optional override for launch mode state
 * @returns {number} - Total discount percentage
 */
export const getTotalDiscountPercentage = (crafterDiscount = 0, launchModeOverride = null) => {
  const crafterPercent = crafterDiscount ? parseFloat(crafterDiscount) / 100 : 0;
  const launchPercent = getLaunchDiscountPercentage(launchModeOverride);
  
  // Combined discount: 1 - (1 - a) * (1 - b)
  const totalDiscount = 1 - (1 - crafterPercent) * (1 - launchPercent);
  return Math.round(totalDiscount * 100);
};

/**
 * Calculate the full price (without any discounts) - used to show "original" price
 * @param {number} basePrice - The price entered by the crafter
 * @returns {number} - The same price (it's the base price)
 */
export const calculateFullPrice = (basePrice) => {
  if (!basePrice || basePrice <= 0) return 0;
  return Math.round(parseFloat(basePrice) * 100) / 100;
};

/**
 * Calculate the price before crafter discount (but after launch discount if active)
 * Used to show what the price would be without crafter's personal discount
 * @param {number} basePrice - The price entered by the crafter
 * @returns {number} - Price with only launch discount applied (if active)
 */
export const calculatePriceBeforeCrafterDiscount = (basePrice) => {
  if (!basePrice || basePrice <= 0) return 0;
  const price = parseFloat(basePrice);
  // Don't apply launch discount here - this shows the "original" price
  return Math.round(price * 100) / 100;
};

/**
 * Calculate the launch discount amount for a given price
 * @param {number} basePrice - The base price
 * @param {number} crafterDiscount - Crafter discount percentage (0-100)
 * @returns {number} - The launch discount amount in euros
 */
export const calculateLaunchDiscount = (basePrice, crafterDiscount = 0) => {
  if (!isLaunchModeActive()) return 0;
  if (!basePrice || basePrice <= 0) return 0;
  
  const price = parseFloat(basePrice);
  const crafterDiscountPercent = crafterDiscount ? parseFloat(crafterDiscount) / 100 : 0;
  
  // Price after crafter discount
  const priceAfterCrafterDiscount = price * (1 - crafterDiscountPercent);
  // Launch discount is applied on top
  const launchDiscountAmount = priceAfterCrafterDiscount * LAUNCH_DISCOUNT_PERCENTAGE;
  
  return Math.round(launchDiscountAmount * 100) / 100;
};

/**
 * Calculate what the crafter will receive after fees are deducted
 * @param {number} finalPrice - The price shown to customers (entered by crafter)
 * @returns {number} - What the crafter will actually receive
 */
export const calculateCrafterEarnings = (finalPrice) => {
  if (!finalPrice || finalPrice <= 0) return 0;
  const price = parseFloat(finalPrice);
  const feePercentage = getCurrentFeePercentage();
  const earnings = price * (1 - feePercentage);
  return Math.round(earnings * 100) / 100;
};

/**
 * Calculate the net price from a final price (what crafter receives)
 * @param {number} finalPrice - The price shown to customers
 * @returns {number} - The net price for the crafter (after fee deduction)
 */
export const calculateNetPrice = (finalPrice) => {
  return calculateCrafterEarnings(finalPrice);
};

/**
 * Calculate the fee amount that will be deducted from the final price
 * @param {number} finalPrice - The price shown to customers
 * @returns {number} - The total fee amount (deducted from final price)
 */
export const calculateFeeAmount = (finalPrice) => {
  if (!finalPrice || finalPrice <= 0) return 0;
  const price = parseFloat(finalPrice);
  const feePercentage = getCurrentFeePercentage();
  const fee = price * feePercentage;
  return Math.round(fee * 100) / 100;
};

/**
 * Get fee breakdown - NEW MODEL
 * @param {number} finalPrice - The price entered by the crafter (what customer sees)
 * @returns {object} - Breakdown of fees
 */
export const getFeeBreakdown = (finalPrice) => {
  if (!finalPrice || finalPrice <= 0) {
    return {
      finalPrice: 0,
      feeAmount: 0,
      crafterEarnings: 0,
      feePercentage: getCurrentFeePercentage(),
      isLaunchMode: isLaunchModeActive(),
      // Legacy fields for compatibility
      netPrice: 0,
      platformFee: 0,
      stripeFee: 0,
      totalFee: 0,
      launchDiscount: 0,
      fullPrice: 0
    };
  }
  
  const price = parseFloat(finalPrice);
  const launchMode = isLaunchModeActive();
  const feePercentage = getCurrentFeePercentage();
  const feeAmount = Math.round(price * feePercentage * 100) / 100;
  const crafterEarnings = Math.round(price * (1 - feePercentage) * 100) / 100;
  
  return {
    finalPrice: price,
    feeAmount,
    crafterEarnings,
    feePercentage,
    isLaunchMode: launchMode,
    // Legacy fields for compatibility (mapped to new model)
    netPrice: crafterEarnings,
    platformFee: feeAmount,
    stripeFee: 0, // No longer separate
    totalFee: feeAmount,
    launchDiscount: 0, // Not applicable in new model
    fullPrice: price
  };
};

// Export constants for reference
export const FEE_CONSTANTS = {
  PERCENTAGE: RARIFEX_FEE_PERCENTAGE,
  PERCENTAGE_DISPLAY: '8%',
  MIN_FEE_EUR: 1,
  MIN_FEE_DISPLAY: '€1',
  FIXED_FEE: 0, // Removed
  FIXED_FEE_DISPLAY: '' // Removed
};
